import React, { useState, useEffect } from 'react';
import { Settings, Play, Loader, Check, X, AlertCircle, Film, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QUALITY_OPTIONS = [
  { value: '360p', label: '360p SD', description: 'Low bandwidth' },
  { value: '480p', label: '480p SD', description: 'Standard definition' },
  { value: '720p', label: '720p HD', description: 'High definition' },
  { value: '1080p', label: '1080p Full HD', description: 'Full HD quality' },
];

const TranscodingManager = ({ authHeader, onTranscodeComplete }) => {
  const [videos, setVideos] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedQualities, setSelectedQualities] = useState(['720p', '480p']);
  const [loading, setLoading] = useState(false);
  const [transcoding, setTranscoding] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchVideos();
    fetchJobs();
    
    // Poll for job updates
    const interval = setInterval(fetchJobs, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchVideos = async () => {
    setLoading(true);
    try {
      // Fetch movies and episodes that can be transcoded
      const [moviesRes, showsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/movies`),
        fetch(`${BACKEND_URL}/api/tv-shows`)
      ]);

      const movies = await moviesRes.json();
      const shows = await showsRes.json();

      const transcodableVideos = [];

      // Add movies with local video URLs
      movies.forEach(movie => {
        if (movie.video_url && movie.video_url.startsWith('/uploads/')) {
          transcodableVideos.push({
            id: movie.id,
            title: movie.title,
            type: 'movie',
            video_url: movie.video_url,
            thumbnail: movie.poster || movie.thumbnail
          });
        }
      });

      // Add episodes with local video URLs
      shows.forEach(show => {
        (show.episodes || []).forEach(episode => {
          if (episode.video_url && episode.video_url.startsWith('/uploads/')) {
            transcodableVideos.push({
              id: episode.id,
              title: `${show.title} - S${episode.season}E${episode.episode_number}: ${episode.title}`,
              type: 'episode',
              video_url: episode.video_url,
              thumbnail: episode.thumbnail
            });
          }
        });
      });

      setVideos(transcodableVideos);
    } catch (err) {
      console.error('Error fetching videos:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/transcode/jobs`,
        { headers: authHeader.headers }
      );
      if (response.ok) {
        const data = await response.json();
        setJobs(data.jobs || []);
      }
    } catch (err) {
      console.error('Error fetching jobs:', err);
    }
  };

  const toggleQuality = (quality) => {
    setSelectedQualities(prev => 
      prev.includes(quality)
        ? prev.filter(q => q !== quality)
        : [...prev, quality]
    );
  };

  const startTranscode = async () => {
    if (!selectedVideo || selectedQualities.length === 0) {
      setError('Select a video and at least one quality');
      return;
    }

    setTranscoding(true);
    setError('');

    try {
      const params = new URLSearchParams();
      selectedQualities.forEach(q => params.append('qualities', q));

      const response = await fetch(
        `${BACKEND_URL}/api/admin/transcode/${selectedVideo.id}?${params}`,
        {
          method: 'POST',
          headers: authHeader.headers
        }
      );

      const data = await response.json();

      if (response.ok) {
        setSelectedVideo(null);
        fetchJobs();
        if (onTranscodeComplete) {
          onTranscodeComplete(data);
        }
      } else {
        setError(data.detail || 'Transcode failed to start');
      }
    } catch (err) {
      setError('Failed to start transcode: ' + err.message);
    } finally {
      setTranscoding(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <span className="bg-green-500/20 text-green-400 px-2 py-1 rounded text-xs">Completed</span>;
      case 'processing':
        return <span className="bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded text-xs flex items-center gap-1"><Loader size={12} className="animate-spin" /> Processing</span>;
      case 'pending':
        return <span className="bg-blue-500/20 text-blue-400 px-2 py-1 rounded text-xs">Pending</span>;
      case 'failed':
        return <span className="bg-red-500/20 text-red-400 px-2 py-1 rounded text-xs">Failed</span>;
      default:
        return <span className="bg-gray-500/20 text-gray-400 px-2 py-1 rounded text-xs">{status}</span>;
    }
  };

  return (
    <div className="bg-gray-900 rounded-xl border border-gray-800 p-6" data-testid="transcoding-manager">
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-purple-600/20 p-3 rounded-lg">
          <Settings className="text-purple-500" size={24} />
        </div>
        <div>
          <h3 className="text-xl font-bold text-white">Video Transcoding</h3>
          <p className="text-gray-400 text-sm">Convert videos to multiple quality levels</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Video Selection */}
        <div>
          <h4 className="text-white font-medium mb-3">Select Video to Transcode</h4>
          
          {loading ? (
            <div className="text-gray-400 py-8 text-center">
              <Loader size={24} className="animate-spin mx-auto mb-2" />
              Loading videos...
            </div>
          ) : videos.length === 0 ? (
            <div className="text-gray-500 py-8 text-center bg-gray-800/50 rounded-lg">
              <Film size={32} className="mx-auto mb-2 opacity-50" />
              <p>No local videos available for transcoding</p>
              <p className="text-xs mt-1">Upload videos via the CMS first</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {videos.map(video => (
                <button
                  key={video.id}
                  onClick={() => setSelectedVideo(video)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
                    selectedVideo?.id === video.id
                      ? 'bg-purple-600/20 border-purple-500'
                      : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
                  }`}
                >
                  {video.thumbnail ? (
                    <img src={video.thumbnail} alt="" className="w-16 h-10 rounded object-cover" />
                  ) : (
                    <div className="w-16 h-10 rounded bg-gray-700 flex items-center justify-center">
                      <Film size={16} className="text-gray-500" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{video.title}</p>
                    <p className="text-gray-500 text-xs capitalize">{video.type}</p>
                  </div>
                  {selectedVideo?.id === video.id && (
                    <Check size={18} className="text-purple-500" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Quality Selection */}
        <div>
          <h4 className="text-white font-medium mb-3">Output Qualities</h4>
          <div className="space-y-2 mb-6">
            {QUALITY_OPTIONS.map(option => (
              <button
                key={option.value}
                onClick={() => toggleQuality(option.value)}
                className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors ${
                  selectedQualities.includes(option.value)
                    ? 'bg-purple-600/20 border-purple-500'
                    : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
                }`}
              >
                <div className="text-left">
                  <p className="text-white text-sm font-medium">{option.label}</p>
                  <p className="text-gray-500 text-xs">{option.description}</p>
                </div>
                {selectedQualities.includes(option.value) ? (
                  <Check size={18} className="text-purple-500" />
                ) : (
                  <div className="w-5 h-5 rounded border border-gray-600" />
                )}
              </button>
            ))}
          </div>

          {/* Start Transcode Button */}
          <button
            onClick={startTranscode}
            disabled={transcoding || !selectedVideo || selectedQualities.length === 0}
            className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2"
            data-testid="start-transcode-btn"
          >
            {transcoding ? (
              <>
                <Loader size={20} className="animate-spin" />
                Starting Transcode...
              </>
            ) : (
              <>
                <Play size={20} />
                Start Transcode
              </>
            )}
          </button>
        </div>
      </div>

      {/* Active Jobs */}
      {jobs.length > 0 && (
        <div className="mt-6 pt-6 border-t border-gray-800">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-white font-medium">Transcode Jobs</h4>
            <button
              onClick={fetchJobs}
              className="text-gray-400 hover:text-white p-1"
            >
              <RefreshCw size={16} />
            </button>
          </div>
          <div className="space-y-2">
            {jobs.slice(0, 5).map(job => (
              <div
                key={job.video_id}
                className="flex items-center justify-between bg-gray-800/50 rounded-lg p-3"
              >
                <div>
                  <p className="text-white text-sm">{job.video_id}</p>
                  <p className="text-gray-500 text-xs">
                    {job.qualities?.join(', ')} • {new Date(job.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {job.progress > 0 && job.status === 'processing' && (
                    <span className="text-gray-400 text-xs">{job.progress}%</span>
                  )}
                  {getStatusBadge(job.status)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TranscodingManager;
